import { FirestoreCollections, ID, Task, Timestamp } from '@griply/types';
import { withRoles } from '@griply/utils';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { stripUndefined } from '../utils/strip-undefined';

export type DuplicateTasksOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TDb = FirestoreInstance,
> = {
  tasks: Task[];
  userId: ID;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: ID };
  db: TDb;
  serverTimestamp: () => TFieldValue;
  writeBatch: (db: NoInfer<TDb>) => {
    set: (ref: NoInfer<TDocRef>, data: Record<string, unknown>) => void;
    commit: () => Promise<void>;
  };
  onWriteError: (error: unknown) => void;
};

const dedupeById = <T extends { id: ID }>(items: T[]): T[] => {
  const seen = new Set<ID>();
  const result: T[] = [];
  items.forEach((item) => {
    if (seen.has(item.id)) {
      return;
    }
    seen.add(item.id);
    result.push(item);
  });
  return result;
};

const remapIds = (
  ids: ID[] | null | undefined,
  oldIdToNewRef: Map<ID, { id: ID }>,
  { keepUnmapped }: { keepUnmapped: boolean },
): ID[] | null => {
  if (!ids) {
    return null;
  }
  const remapped = ids
    .map((id) => oldIdToNewRef.get(id)?.id ?? (keepUnmapped ? id : undefined))
    .filter((id): id is ID => !!id);
  return remapped.length ? remapped : null;
};

export const duplicateTasks = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TDb = FirestoreInstance,
>(
  options: DuplicateTasksOptions<TDocRef, TCollectionRef, TFieldValue, TDb>,
): Promise<Task[]> => {
  const {
    tasks,
    userId,
    collection,
    doc,
    db,
    serverTimestamp,
    writeBatch,
    onWriteError,
  } = options;

  if (!tasks.length) {
    return [];
  }

  const tasksToDuplicate = dedupeById(tasks);
  const taskCollection = collection(db, FirestoreCollections.Tasks);

  const oldIdToNewRef = new Map<ID, TDocRef & { id: ID }>();
  tasksToDuplicate.forEach((task) => {
    oldIdToNewRef.set(task.id, doc(taskCollection));
  });

  const batch = writeBatch(db);
  const newTasks: Task[] = [];

  tasksToDuplicate.forEach((task) => {
    const docRef = oldIdToNewRef.get(task.id);
    if (!docRef) {
      return;
    }

    const newChildIds = remapIds(task.childIds, oldIdToNewRef, {
      keepUnmapped: false,
    });
    const newParentIds = remapIds(task.parentIds, oldIdToNewRef, {
      keepUnmapped: true,
    });
    const newSubtaskOrder =
      remapIds(task.subtaskOrder, oldIdToNewRef, { keepUnmapped: false }) ??
      newChildIds;

    const newTask = withRoles(
      {
        ...task,
        id: docRef.id,
        createdAt: serverTimestamp() as unknown as Timestamp,
        updatedAt: serverTimestamp() as unknown as Timestamp,
        childIds: newChildIds,
        parentIds: newParentIds,
        subtaskOrder: newSubtaskOrder,
        frozenAt: null,
        excludedFromLimits: null,
      },
      userId,
    ) as Task;

    batch.set(docRef, stripUndefined(newTask));
    newTasks.push(newTask);
  });

  detachWrite(batch.commit(), onWriteError);
  return newTasks;
};
